import i18n, { t } from "i18next";
import { CheckIcon, MinusIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProductDefinitionParameterRaw } from "@/types/insurance/productDefinition";

// Parameter types whose value is a boolean toggle.
const BOOLEAN_TYPES = new Set([
	"min-invalidity",
	"dependents-supplement",
	"fixed-dependents-supplement",
	"child-supplement",
	"child-supplement-coordination",
	"funeral-allowance",
	"fixed-dependents-allowance",
]);

// Parameter types that are sub-params rendered inside a composite parent card in
// the editing UI. We show them independently here since there is no parent card.
// (No types are excluded — all params are shown on the catalog page.)

// The short label shown on the right side of each numeric input — mirrors the
// labels used in the editing components so the catalog looks familiar.
const SIDE_LABEL: Partial<Record<string, string>> = {
	"base-compensation": "G",
	"annual-salary": "x",
	"annual-salary-above-x-g": "AB",
	reduction: "% A",
	increase: "% O",
	"increase-before-34": "% O34",
	"max-compensation": "G Max",
	"min-compensation": "G Min",
	"for-number-of-years": "x",
	"reduction-in-g": "G O",
	"if-non-provider-compensation": "G NP",
	"base-compensation-child-supplement": "G BT",
	"base-compensation-dependents-supplement": "G FT",
	"coordination-amount": "G Sam",
};

// Side labels that come from the translation file (age-related params).
const TRANSLATED_SIDE_LABEL: Partial<Record<string, string>> = {
	"max-age": "admin.insurances.parameters.type-to-name.max-age",
	"min-age": "admin.insurances.parameters.type-to-name.min-age",
	"start-age": "admin.insurances.parameters.type-to-name.s",
	"compensation-taper-start-age": "admin.insurances.parameters.type-to-name.s",
	"end-age": "admin.insurances.parameters.type-to-name.e",
	"max-age-child": "admin.insurances.parameters.type-to-name.max-age-child",
	"max-age-child-supplement":
		"admin.insurances.parameters.type-to-name.max-age-child-supplement",
	"min-age-child-supplement":
		"admin.insurances.parameters.type-to-name.min-age-child-supplement",
	"max-age-dependents-supplement":
		"admin.insurances.parameters.type-to-name.max-age",
	"min-age-dependents-supplement":
		"admin.insurances.parameters.type-to-name.min-age",
};

const getSideLabel = (type: string): string => {
	if (SIDE_LABEL[type]) return SIDE_LABEL[type];
	const key = TRANSLATED_SIDE_LABEL[type];
	if (key) {
		if (i18n.exists(key)) return i18n.t(key);
		return type;
	}
	return type;
};

const getParamName = (type: string): string => {
	const paramKey = `admin.insurances.parameters.type-to-name.${type}`;
	if (i18n.exists(paramKey)) {
		return i18n.t(paramKey);
	}
	return type;
};

const isIntervalArray = (
	value: ProductDefinitionParameterRaw["value"],
): value is ReadonlyArray<{ minAge: string; maxAge: string; value: string }> =>
	Array.isArray(value);

interface Props {
	parameter: ProductDefinitionParameterRaw;
}

export const ParameterDisplay = ({ parameter }: Props) => {
	const { type, value } = parameter;
	const name = getParamName(type);

	// ── Intervals ────────────────────────────────────────────────────────────
	if (type === "intervals" && isIntervalArray(value)) {
		return (
			<div className="rounded-md border border-input bg-background px-2 py-1.5 text-sm w-full">
				<p className="text-xs font-medium text-muted-foreground mb-1.5">
					{name}
				</p>
				{value.length === 0 ? (
					<p className="text-xs text-muted-foreground italic">—</p>
				) : (
					<table className="w-full text-xs">
						<thead>
							<tr className="text-muted-foreground">
								<th className="text-left font-normal pb-1">
									{t("admin.insurances.parameters.type-to-name.min-age")}
								</th>
								<th className="text-left font-normal pb-1">
									{t("admin.insurances.parameters.type-to-name.max-age")}
								</th>
								<th className="text-left font-normal pb-1">
									{t("admin.insurances.parameters.type-to-name.annual-salary")}
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{value.map((row, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: intervals have no stable id in raw form
								<tr key={i}>
									<td className="py-0.5 pr-3">{row.minAge || "—"}</td>
									<td className="py-0.5 pr-3">{row.maxAge || "—"}</td>
									<td className="py-0.5">{row.value || "—"}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		);
	}

	// ── Boolean toggles ───────────────────────────────────────────────────────
	if (BOOLEAN_TYPES.has(type) || typeof value === "boolean") {
		const isOn = value === true;
		return (
			<div
				className={cn(
					"flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm",
					isOn
						? "border-moss-green-80/30 bg-moss-green-80/5"
						: "border-input bg-background",
				)}
			>
				<span
					className={cn(
						"inline-flex h-4 w-4 items-center justify-center rounded-sm border text-xs shrink-0",
						isOn
							? "border-moss-green-80 bg-moss-green-80 text-white"
							: "border-input bg-muted text-muted-foreground",
					)}
				>
					{isOn ? (
						<CheckIcon className="h-3 w-3" />
					) : (
						<MinusIcon className="h-3 w-3" />
					)}
				</span>
				<span className="text-xs">{name}</span>
			</div>
		);
	}

	// ── Numeric / string ──────────────────────────────────────────────────────
	const displayValue =
		value !== undefined && value !== "" ? String(value) : "—";
	const sideLabel = getSideLabel(type);

	return (
		<div className="relative inline-flex shrink-0">
			<div
				className={cn(
					"flex h-9 w-24 items-center rounded-md rounded-r-none border border-input bg-background px-3 text-sm text-muted-foreground",
					displayValue === "—" && "opacity-50",
				)}
			>
				{displayValue}
			</div>
			<span
				className={cn(
					"inline-flex h-9 items-center rounded-r-md border border-l-0 border-input bg-secondary px-2 text-xs font-medium text-secondary-foreground whitespace-nowrap",
				)}
				title={name}
			>
				{sideLabel}
			</span>
		</div>
	);
};
